/**
 * Minimal fetch helper that always includes the portal session cookie and
 * normalises JSON responses. The backend lives behind the Vite proxy
 * during dev (see vite.config.ts) and on the same origin in production.
 *
 * The helper deliberately rejects non-JSON success responses: when the
 * Vite dev proxy can't reach the upstream API it serves the SPA's
 * index.html with status 200, which would otherwise be silently parsed
 * as an empty object by callers (manifesting as "no data" instead of
 * "the API is unreachable").
 */
export async function http<T>(
  input: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers || {}),
    },
  });

  const text = await response.text();
  const contentType = response.headers.get("content-type") ?? "";
  const looksJson = contentType.toLowerCase().includes("json");
  const data = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    const message =
      (data && typeof data === "object" && "error" in (data as any)
        ? (data as any).error
        : null) || response.statusText || "Request failed";
    const err = new HttpError(message, response.status, data);
    throw err;
  }

  // Some hosts return JSON with a generic Content-Type; accept a parsed object
  // even when the header does not mention "json".
  if (text && !looksJson) {
    if (typeof data === "object" && data !== null) {
      return data as T;
    }
    if (typeof data === "string") {
      const probe = data.trimStart();
      if (
        probe.startsWith("<!") ||
        probe.toLowerCase().startsWith("<html") ||
        probe.toLowerCase().startsWith("<!doctype")
      ) {
        throw new HttpError(
          "The API returned a web page instead of JSON. Restart or redeploy Aivrmanager.Api after pulling new routes, and ensure the dev proxy targets the same port as Kestrel (see vite.config.ts).",
          response.status,
          data,
        );
      }
      throw new HttpError(
        "API returned a non-JSON response (is the backend running?).",
        response.status,
        data,
      );
    }
  }

  return data as T;
}

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public data: unknown,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
